import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

import { useAuthStore } from '@/stores/auth'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true }
  },
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/HomeView.vue')
  },
  // Operator routes (lazy-loaded — keeps learner bundle small)
  {
    // /generator — the unified front door (additive). Orchestrates the two
    // existing zip-upload clients (Door A conversions, Door B scorm) in one
    // stepped flow; adds no backend and edits neither existing door. Role list
    // is the union of the two doors it unifies (op-upload's operator/admin and
    // op-course-new's operator/editor/senior_editor/admin).
    path: '/generator',
    name: 'generator',
    component: () => import('@/views/GeneratorView.vue'),
    meta: { roles: ['operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/op/upload',
    name: 'op-upload',
    component: () => import('@/views/operator/UploadView.vue'),
    meta: { roles: ['operator', 'admin'] }
  },
  {
    path: '/op/sessions',
    name: 'op-sessions',
    component: () => import('@/views/operator/SessionsView.vue'),
    meta: { roles: ['operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/op/improvements',
    name: 'op-improvements',
    component: () => import('@/views/operator/ImprovementsView.vue'),
    meta: { roles: ['operator', 'editor', 'senior_editor', 'admin', 'learner'] }
  },
  {
    path: '/op/courses',
    name: 'op-courses',
    component: () => import('@/views/operator/CoursesView.vue'),
    meta: { roles: ['operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/op/courses/new',
    name: 'op-course-new',
    component: () => import('@/views/operator/NewCourseView.vue'),
    meta: { roles: ['operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/op/courses/:id',
    name: 'op-course-detail',
    component: () => import('@/views/operator/CourseDetailView.vue'),
    meta: { roles: ['operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/op/sessions/:id',
    name: 'op-session-detail',
    component: () => import('@/views/operator/SessionDetailView.vue'),
    meta: { roles: ['operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/op/settings',
    component: () => import('@/views/operator/SettingsView.vue'),
    meta: { roles: ['operator', 'editor', 'senior_editor', 'admin'] },
    redirect: '/op/settings/general',
    children: [
      {
        path: 'general',
        name: 'op-settings-general',
        component: () => import('@/views/operator/settings/GeneralSettings.vue')
      },
      {
        path: 'ai-models',
        name: 'op-settings-ai-models',
        component: () => import('@/views/operator/settings/AiModelsSettings.vue')
      },
      {
        path: 'prompt-templates',
        name: 'op-settings-prompt-templates',
        component: () =>
          import('@/views/operator/settings/PromptTemplatesSettings.vue')
      },
      {
        path: 'profiles',
        name: 'op-settings-profiles',
        component: () => import('@/views/operator/settings/ProfilesSettings.vue')
      },
      {
        path: 'diagnostics',
        name: 'op-settings-diagnostics',
        component: () =>
          import('@/views/operator/settings/DiagnosticsSettings.vue')
      },
      {
        path: 'xp-weights',
        name: 'op-settings-xp-weights',
        component: () =>
          import('@/views/operator/settings/XpWeightsSettings.vue')
      },
      {
        path: 'trash',
        name: 'op-settings-trash',
        component: () =>
          import('@/views/operator/settings/TrashSettings.vue')
      },
      {
        path: 'gameplay-engine',
        name: 'op-settings-gameplay-engine',
        component: () =>
          import('@/views/operator/settings/GameplayEngineSettings.vue')
      },
      {
        path: 'gameplay-engine/games/:gameId',
        name: 'op-settings-gameplay-game-detail',
        component: () =>
          import('@/views/operator/settings/GameplayGameDetail.vue')
      },
      {
        path: 'upload-diagnostic',
        name: 'op-settings-upload-diagnostic',
        component: () =>
          import('@/views/operator/settings/UploadDiagnosticSettings.vue')
      }
    ]
  },
  {
    path: '/op/sessions/:id/processing',
    name: 'op-processing',
    component: () => import('@/views/operator/ProcessingView.vue'),
    meta: { roles: ['operator', 'admin'] }
  },
  {
    path: '/op/sessions/:id/review',
    name: 'op-review',
    component: () => import('@/views/operator/OperatorReviewView.vue'),
    meta: { roles: ['operator', 'admin'] }
  },
  {
    path: '/op/sessions/:id/handouts',
    name: 'op-handouts',
    component: () => import('@/views/operator/HandoutsView.vue'),
    meta: { roles: ['operator', 'admin'] }
  },
  {
    path: '/op/sessions/:id/handouts/:hid',
    name: 'op-handout-edit',
    component: () => import('@/views/operator/HandoutEditView.vue'),
    meta: { roles: ['operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/op/sessions/:id/concepts',
    name: 'op-concepts-editor',
    component: () => import('@/views/operator/ConceptsEditorView.vue'),
    meta: { roles: ['operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/op/sessions/:id/quiz',
    name: 'op-quiz-editor',
    component: () => import('@/views/operator/QuizEditorView.vue'),
    meta: { roles: ['operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/op/sessions/:id/reinforcement',
    name: 'op-reinforcement-editor',
    component: () => import('@/views/operator/ReinforcementEditorView.vue'),
    meta: { roles: ['operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/op/sessions/:id/transcript',
    name: 'op-transcript-editor',
    component: () => import('@/views/operator/TranscriptEditorView.vue'),
    meta: { roles: ['operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    // Dedicated, full-page JSON viewer for a converted package's canonical
    // IR — supersedes NativeJsonViewer.vue's inline per-lesson expander
    // (components/operator/) for READING; that component still owns Copy/
    // Download from inside SessionDetailView.vue's "Native conversion"
    // panel and now links here instead of expanding inline. Same role list
    // as op-session-detail (the page that links to it, and the only page
    // that can trigger the convert this JSON is a product of) — see
    // router/__tests__/index.spec.ts for the parity check.
    path: '/op/sessions/:id/json',
    name: 'op-native-json',
    component: () => import('@/views/operator/NativeJsonView.vue'),
    meta: { roles: ['operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/op/needs-review',
    name: 'op-needs-review',
    component: () => import('@/views/operator/NeedsReviewView.vue'),
    meta: { roles: ['operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/op/analytics',
    name: 'op-analytics',
    component: () => import('@/views/operator/AnalyticsView.vue'),
    meta: { roles: ['admin'] }
  },
  {
    path: '/op/celebration-assets',
    name: 'op-celebration-assets',
    component: () => import('@/views/operator/CelebrationAssetsView.vue'),
    meta: { roles: ['admin'] }
  },
  {
    path: '/op/celebration-config',
    name: 'op-celebration-config',
    component: () => import('@/views/operator/CelebrationConfigView.vue'),
    meta: { roles: ['admin'] }
  },
  {
    path: '/op/marketplace',
    name: 'op-marketplace',
    component: () => import('@/views/operator/MarketplaceView.vue'),
    meta: { roles: ['operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/op/marketplace/:id',
    name: 'op-marketplace-detail',
    component: () => import('@/views/operator/MarketplaceDetailView.vue'),
    meta: { roles: ['operator', 'editor', 'senior_editor', 'admin'] }
  },
  // Learner routes (eager — common path)
  {
    path: '/learn',
    name: 'learn-home',
    component: () => import('@/views/learner/LearnerHomeView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/reinforcement/:id',
    name: 'learn-queued-reinforcement',
    component: () => import('@/views/learner/QueuedReinforcementView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/courses/:id',
    name: 'learn-course-hub',
    component: () => import('@/views/learner/CourseHubView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/badges',
    name: 'learn-badges',
    component: () => import('@/views/learner/BadgesView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/leaderboards',
    name: 'learn-leaderboards',
    component: () => import('@/views/learner/LeaderboardsView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id',
    name: 'learn-session-hub',
    component: () => import('@/views/learner/SessionHubView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/player',
    name: 'learn-player',
    component: () => import('@/views/learner/PlayerView.vue'),
    meta: { roles: ['learner', 'operator', 'admin'] }
  },
  {
    // Task 10 of the SCORM/xAPI ingestion feature: closes the gap an
    // independent review found — ScormPlayer.vue (Task 6/9) was imported by
    // no view and no route. Resolves the session's package via GET
    // .../scorm-package, mints its launch URL, and mounts ScormPlayer.
    path: '/learn/sessions/:id/package',
    name: 'learn-scorm-package',
    component: () => import('@/views/learner/ScormPackageView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    // Task 5 of the native block-conversion plan
    // (docs/superpowers/plans/2026-08-04-native-block-conversion.md): the
    // SECOND way to consume a converted package, alongside (never instead
    // of) learn-scorm-package just above. Same role list, deliberately —
    // an editor/senior_editor who can trigger a conversion (Task 3's
    // convert endpoint) must also be able to open what it produced.
    path: '/learn/sessions/:id/native',
    name: 'learn-native-course',
    component: () => import('@/views/learner/NativeCourseView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/quiz',
    name: 'learn-quiz',
    component: () => import('@/views/learner/QuizPlayerView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/reinforcement',
    name: 'learn-reinforcement',
    component: () => import('@/views/learner/ReinforcementPlayerView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/flashcards',
    name: 'learn-flashcards',
    component: () => import('@/views/learner/FlashcardsView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/wheel',
    name: 'learn-wheel',
    component: () => import('@/views/learner/WheelView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/word-search',
    name: 'learn-word-search',
    component: () => import('@/views/learner/WordSearchView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/matching',
    name: 'learn-matching',
    component: () => import('@/views/learner/MatchingView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/ordering',
    name: 'learn-ordering',
    component: () => import('@/views/learner/OrderingView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/dose-calc',
    name: 'learn-dose-calc',
    component: () => import('@/views/learner/DoseCalcView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/crossword',
    name: 'learn-crossword',
    component: () => import('@/views/learner/CrosswordView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/simulations',
    name: 'learn-simulations',
    component: () => import('@/views/learner/SimulationPlayerView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/simulations/:simId',
    name: 'learn-simulation-play',
    component: () => import('@/views/learner/SimulationPlayerView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/crisis-sim',
    name: 'learn-crisis-sim',
    component: () => import('@/views/learner/CrisisSimView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/sepsis-resus',
    name: 'learn-sepsis-resus',
    component: () => import('@/views/learner/SepsisResusView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/toxin-timeline',
    name: 'learn-toxin-timeline',
    component: () => import('@/views/learner/ToxinTimelineView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/radiation-dosimetry',
    name: 'learn-radiation-dosimetry',
    component: () => import('@/views/learner/RadiationDosimetryView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/er-triage',
    name: 'learn-er-triage',
    component: () => import('@/views/learner/TriageQueueView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/hemorrhage-shock',
    name: 'learn-hemorrhage-shock',
    component: () => import('@/views/learner/HemorrhageShockView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/multi-drug-interaction',
    name: 'learn-multi-drug-interaction',
    component: () => import('@/views/learner/MultiDrugInteractionView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/bloodborne-pathogen',
    name: 'learn-bloodborne-pathogen',
    component: () => import('@/views/learner/BloodbornePathogenView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/fire-evac',
    name: 'learn-fire-evac',
    component: () => import('@/views/learner/FireEvacView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/or-throughput',
    name: 'learn-or-throughput',
    component: () => import('@/views/learner/OrThroughputView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/hazcom-drill',
    name: 'learn-hazcom-drill',
    component: () => import('@/views/learner/HazcomDrillView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/waste-anesthetic-gas',
    name: 'learn-waste-anesthetic-gas',
    component: () => import('@/views/learner/WasteAnestheticGasView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/lockout-tagout',
    name: 'learn-lockout-tagout',
    component: () => import('@/views/learner/LockoutTagoutView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/chemical-splash',
    name: 'learn-chemical-splash',
    component: () => import('@/views/learner/ChemicalSplashView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  },
  {
    path: '/learn/sessions/:id/aggressive-animal',
    name: 'learn-aggressive-animal',
    component: () => import('@/views/learner/AggressiveAnimalView.vue'),
    meta: { roles: ['learner', 'operator', 'editor', 'senior_editor', 'admin'] }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  // If the API client cleared the stored token on a 401 (mid-session JWT
  // expiry), drop the stale in-memory session so the guard below redirects
  // to /login instead of navigating a dead shell.
  auth.syncFromStorage()

  if (auth.token && !auth.user && !auth.bootstrapping) {
    await auth.bootstrap()
  }

  if (to.meta?.public) {
    return true
  }

  if (!auth.isAuthenticated) {
    // Don't litter the URL with ?next=/ — `/` is already the post-login default.
    // Only attach `next` when the requested route is something other than home.
    if (to.fullPath === '/' || to.fullPath === '') {
      return { name: 'login' }
    }
    return { name: 'login', query: { next: to.fullPath } }
  }

  const requiredRoles = (to.meta?.roles as string[] | undefined) ?? []
  if (requiredRoles.length > 0 && auth.user) {
    if (!requiredRoles.includes(auth.user.role)) {
      return { name: 'home' }
    }
  }
  return true
})

export default router
